import { useMemo, useState } from "react";
import { libraryFilters, libraryItems } from "@/mocks/evidence";
import Reveal from "./Reveal";
import { SectionHeading, StatusBadge } from "./Ui";

const catIcon: Record<string, string> = {
  截图: "ri-file-image-line",
  录音: "ri-disc-line",
  视频: "ri-clapperboard-line",
  文件: "ri-file-list-3-line",
};

export default function EvidenceLibrary() {
  const [filter, setFilter] = useState("全部");
  const [q, setQ] = useState("");
  const [saved, setSaved] = useState<string[]>([]);

  const items = useMemo(() => {
    const query = q.trim();
    return libraryItems.filter((it) => {
      const okCat = filter === "全部" || it.category === filter;
      const okQ =
        query === "" ||
        it.title.includes(query) ||
        it.category.includes(query) ||
        it.status.includes(query);
      return okCat && okQ;
    });
  }, [filter, q]);

  const toggleSave = (id: string) =>
    setSaved((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <section id="library" className="bg-background-100">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Evidence Library · 证据库"
          title="截图 · 录音 · 视频 · 文件"
          desc="石锤材料按类别与核验状态归档。每一条都标注来源类别与结论等级，可切换分类或输入关键词即时检索。"
        />

        <div className="mb-7 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div
            role="tablist"
            aria-label="材料分类"
            className="inline-flex flex-wrap items-center gap-1 self-start rounded-full border border-background-200 bg-background-50 p-1"
          >
            {libraryFilters.map((f) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                onClick={() => setFilter(f)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  filter === f
                    ? "bg-primary-600 text-background-50"
                    : "text-foreground-700 hover:bg-background-200/70"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 lg:w-72">
            <i className="ri-search-line flex h-4 w-4 items-center justify-center text-sm text-foreground-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="筛选标题 / 类别 / 状态…"
              aria-label="筛选材料"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="清空"
                className="flex h-5 w-5 items-center justify-center rounded-full bg-background-200 text-foreground-600"
              >
                <i className="ri-close-line text-xs" />
              </button>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-file-search-line flex h-10 w-10 items-center justify-center text-3xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-500">暂无匹配的材料条目</p>
            <button
              type="button"
              onClick={() => {
                setFilter("全部");
                setQ("");
              }}
              className="mt-4 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 hover:bg-background-200/70"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((it, idx) => {
              const isVideo = it.category === "视频" || it.category === "录音";
              const liked = saved.includes(it.id);
              return (
                <Reveal key={it.id} delay={(idx % 3) * 70}>
                  <article className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
                    <div className="relative aspect-[8/5] overflow-hidden bg-background-200">
                      <img
                        src={it.image}
                        alt={it.title}
                        title={`八酱黑料 ${it.category} ${it.title}`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                      {isVideo && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 text-primary-700 backdrop-blur transition-transform group-hover:scale-110">
                            <i className="ri-play-fill ml-0.5 flex h-5 w-5 items-center justify-center text-xl" />
                          </span>
                        </span>
                      )}
                      <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded bg-background-50/90 px-2 py-1 text-[11px] font-semibold text-foreground-800 backdrop-blur">
                        <i className={`${catIcon[it.category]} text-sm`} />
                        {it.category}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSave(it.id);
                        }}
                        aria-label={liked ? "取消收藏" : "收藏"}
                        className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full backdrop-blur transition-colors ${
                          liked
                            ? "bg-accent-500 text-background-50"
                            : "bg-background-50/90 text-foreground-700 hover:bg-background-50"
                        }`}
                      >
                        <i
                          className={`flex h-4 w-4 items-center justify-center ${
                            liked ? "ri-bookmark-fill" : "ri-bookmark-line"
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex flex-1 flex-col p-4 md:p-5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-heading text-sm font-semibold leading-snug text-foreground-950 md:text-base">
                          <a href="#library" className="hover:text-primary-700">
                            {it.title}
                          </a>
                        </h3>
                        <StatusBadge status={it.status} />
                      </div>
                      <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-calendar-line flex h-4 w-4 items-center justify-center text-sm" />
                          {it.date}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <i className="ri-eye-line flex h-4 w-4 items-center justify-center text-sm" />
                          {it.stat}
                        </span>
                        <span className="inline-flex items-center gap-2 text-foreground-400">
                          <i className="ri-share-forward-line flex h-4 w-4 items-center justify-center text-sm" />
                          <i className="ri-download-2-line flex h-4 w-4 items-center justify-center text-sm" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex items-center justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200/70"
          >
            加载更多材料
            <i className="ri-loader-4-line flex h-4 w-4 items-center justify-center text-sm" />
          </button>
        </div>
      </div>
    </section>
  );
}